import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CompositeScreenProps } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { addDays } from 'date-fns';
import { useQuery, useQueryClient } from 'react-query';
import { Button, HelperText, TextInput, useTheme } from 'react-native-paper';
import { Keyboard } from 'react-native';
import { getTimeZone } from 'react-native-localize';
import { useI18n } from '../../context/I18nContext';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { appsyncGraphql } from '../../apis/appsyncHelper';
import {
  BuildingBuildingStatusArgs,
  QueryGetBuildingsByRegionIdArgs,
  Reservation,
  TimeRangeInput,
} from '../../types/appsync-types';
import { AdminStackRouteParams } from '../../navigators/AdminStackNavigator';
import DateTimePickerModal from '../../components/modal/DateTimePickerModal';
import ReservationListItem from './ReservationListItem';
import Container from '../../components/Container';
import Loading from '../../components/Loading';
import { useMapViewFilter } from '../../context/MapViewFilterContext';
import { startOfDayTz } from '../../utils/date-utils';
import { UiIcon } from '../../components/SimpleListIcon';

const RESERVATIONS_FOR_SPACE_QUERY_ID = 'useReservationsForSpace';
function useReservationsForSpace(spaceId: string, timeInput: TimeRangeInput) {
  return useQuery(
    [RESERVATIONS_FOR_SPACE_QUERY_ID, spaceId, ...Object.values(timeInput)],
    async () => {
      const variables: QueryGetBuildingsByRegionIdArgs & BuildingBuildingStatusArgs = {
        id: spaceId,
        input: timeInput,
      };
      const query = /* GraphQL */ `
        query GetReservationsBySpaceIdQuery($id: ID!, $input: TimeRangeInput) {
          getPoisByID(id: $id) {
            reservations(input: $input) {
              id
              spaceId
              from
              to
              status
              credit
              paymentAmountInCents
              currency
              building {
                title
                timezone
              }
            }
          }
        }
      `;
      return appsyncGraphql<'getPoisByID'>(query, variables);
    },
    { retry: false, enabled: spaceId !== '' },
  );
}

type Props = CompositeScreenProps<
  StackScreenProps<AdminStackRouteParams, 'AdminSpaceReservations'>,
  AppDrawerScreenProp
>;

export const SpaceReservations: React.FC<Props> = () => {
  const { I18n, currentLocale, formatDate } = useI18n();
  const { colors } = useTheme();
  const [spaceId, setSpaceId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);
  const [date, setDate] = useState<Date>(new Date());
  const [spaceIdInput, setSpaceIdInput] = useState<string>('');
  const { timeZone } = useMapViewFilter();

  const queryClient = useQueryClient();
  const spaceQuery = useReservationsForSpace(spaceId, {
    from: startOfDayTz(date, timeZone).toISOString(),
    to: addDays(startOfDayTz(date, timeZone), 1).toISOString(),
  });

  useEffect(() => {
    if (spaceQuery.isError) {
      setErrorMessage(JSON.stringify(spaceQuery.error));
    } else if (spaceQuery.data?.errors) {
      setErrorMessage(JSON.stringify(spaceQuery.data?.errors[0].message));
    } else {
      setErrorMessage(undefined);
    }
  }, [spaceQuery.data?.errors, spaceQuery.error, spaceQuery.isError]);

  /* yes, I know I could use a reducer, but I am a lazy copy-paste coder */
  const reservations = useMemo(() => {
    const res: Reservation[] = [];
    spaceQuery.data?.data?.getPoisByID?.reservations?.forEach((reservation) => {
      if (reservation) res.push(reservation);
    });

    res.sort((a, b) => Date.parse(a.from!) - Date.parse(b.from!));
    return res;
  }, [spaceQuery.data?.data?.getPoisByID?.reservations]);

  const onShowDatePicker = useCallback(() => {
    setDatePickerVisible(true);
  }, []);

  const onDateTimeConfirm = useCallback((newDate: Date) => {
    setDatePickerVisible(false);
    setDate(newDate);
  }, []);

  const onDateTimeCancel = useCallback(() => {
    setDatePickerVisible(false);
  }, []);

  return (
    <>
      <ScrollablePage
        summary={{
          title: spaceId,
          description: I18n.t('locations.region.summaryDescription'),
          icon: 'pin',
          button: {
            label: formatDate(date, 'dateWithYear', getTimeZone()),
            onPress: onShowDatePicker,
          },
        }}
      >
        <Container>
          <TextInput
            placeholder="Enter space id"
            value={spaceIdInput}
            onChangeText={setSpaceIdInput}
            error={!!errorMessage}
            right={errorMessage && <UiIcon name={'exclamation'} color={colors.error} />}
            mode={'outlined'}
            keyboardType={'numeric'}
          />
        </Container>
        <Button
          onPress={() => {
            Keyboard.dismiss();
            queryClient.invalidateQueries(RESERVATIONS_FOR_SPACE_QUERY_ID);
            setSpaceId(spaceIdInput);
          }}
        >
          Update
        </Button>
        <HelperText visible={true} type={'error'}>
          {errorMessage}
        </HelperText>
        {/* Loading */}
        {spaceQuery.isLoading ? <Loading /> : null}
        {/* No results */}
        {reservations?.length === 0 && !spaceQuery.isLoading ? (
          <HelperText visible={true} type={'info'}>
            No results
          </HelperText>
        ) : null}
        {/* Results */}
        {reservations?.map((res) => (res ? <ReservationListItem key={res.id} reservation={res} /> : null))}
      </ScrollablePage>
      <DateTimePickerModal
        isVisible={datePickerVisible}
        mode="date"
        date={date}
        locale={currentLocale}
        onConfirm={onDateTimeConfirm}
        onCancel={onDateTimeCancel}
      />
    </>
  );
};
